import { lazy, Suspense, type ReactNode } from "react";
import { Navigate, Outlet, createBrowserRouter, useLocation } from "react-router-dom";
import { Skeleton } from "@zaphub/ui";

import AuthLayout from "./layouts/AuthLayout";
import PainelLayout from "./layouts/PainelLayout";
import PlataformaLayout from "./layouts/PlataformaLayout";
import { useSessao } from "./lib/sessao";

/**
 * MAPA DE ROTAS — pertence à fundação. Cada área tem páginas em
 * `src/paginas/<area>/` (um arquivo por tela); o pacote da área só edita esses
 * arquivos. Rota nova: peça ao orquestrador (ou acrescente uma linha aqui).
 */
const Entrar = lazy(() => import("./paginas/auth/Entrar"));
const Cadastro = lazy(() => import("./paginas/auth/Cadastro"));
const CompletarCadastro = lazy(() => import("./paginas/auth/CompletarCadastro"));
const EsqueciSenha = lazy(() => import("./paginas/auth/EsqueciSenha"));
const RedefinirSenha = lazy(() => import("./paginas/auth/RedefinirSenha"));
const Dashboard = lazy(() => import("./paginas/dashboard/Dashboard"));
const Sobre = lazy(() => import("./paginas/sobre/Sobre"));
const ListaNumeros = lazy(() => import("./paginas/numeros/ListaNumeros"));
const DetalheNumero = lazy(() => import("./paginas/numeros/DetalheNumero"));
const ListaCampanhas = lazy(() => import("./paginas/campanhas/ListaCampanhas"));
const NovaCampanha = lazy(() => import("./paginas/campanhas/NovaCampanha"));
const DetalheCampanha = lazy(() => import("./paginas/campanhas/DetalheCampanha"));
const Listas = lazy(() => import("./paginas/campanhas/Listas"));
const Leads = lazy(() => import("./paginas/leads/Leads"));
const ConfigRepasse = lazy(() => import("./paginas/leads/ConfigRepasse"));
const Caixa = lazy(() => import("./paginas/caixa/Caixa"));
const AutoRespostas = lazy(() => import("./paginas/caixa/AutoRespostas"));
const Integracoes = lazy(() => import("./paginas/integracoes/Integracoes"));
const Cobranca = lazy(() => import("./paginas/cobranca/Cobranca"));
const BloqueioAssinatura = lazy(() => import("./paginas/cobranca/BloqueioAssinatura"));
const PlataformaVisaoGeral = lazy(() => import("./paginas/plataforma/PlataformaVisaoGeral"));
const PlataformaContas = lazy(() => import("./paginas/plataforma/PlataformaContas"));
const PlataformaDetalheConta = lazy(() => import("./paginas/plataforma/PlataformaDetalheConta"));
const PlataformaPlanos = lazy(() => import("./paginas/plataforma/PlataformaPlanos"));
const PlataformaNumeros = lazy(() => import("./paginas/plataforma/PlataformaNumeros"));
const PlataformaConfig = lazy(() => import("./paginas/plataforma/PlataformaConfig"));
const PlataformaPrecos = lazy(() => import("./paginas/plataforma/PlataformaPrecos"));
const PlataformaAuditoria = lazy(() => import("./paginas/plataforma/PlataformaAuditoria"));

const carregando = (
  <div className="grid gap-3 p-6" role="status" aria-label="Carregando">
    <Skeleton className="h-8 w-48" />
    <Skeleton className="h-40 w-full" />
  </div>
);
const L = (el: ReactNode) => <Suspense fallback={carregando}>{el}</Suspense>;

/** Exige sessão; conta sem assinatura só alcança a tela de cobrança. */
function RotaProtegida({ exigir }: { exigir?: "OWNER" | "SUPERADMIN" }) {
  const { usuario, conta, iniciada } = useSessao();
  const local = useLocation();
  if (!iniciada) return carregando;
  if (!usuario) return <Navigate to="/entrar" state={{ de: local.pathname }} replace />;
  if (exigir && usuario.role !== exigir) {
    return <Navigate to={usuario.role === "SUPERADMIN" ? "/plataforma" : "/"} replace />;
  }
  if (usuario.role === "OWNER" && conta && !conta.assinaturaAtiva && !local.pathname.startsWith("/cobranca")) {
    return <Navigate to="/cobranca/assinatura-necessaria" replace />;
  }
  return <Outlet />;
}

/** Quem já tem sessão não vê as telas de entrada. */
function SoParaVisitantes() {
  const { usuario, iniciada } = useSessao();
  if (!iniciada) return carregando;
  if (usuario) return <Navigate to={usuario.role === "SUPERADMIN" ? "/plataforma" : "/"} replace />;
  return <Outlet />;
}

export const router = createBrowserRouter([
  {
    element: <SoParaVisitantes />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          { path: "/entrar", element: L(<Entrar />) },
          { path: "/cadastro", element: L(<Cadastro />) },
          { path: "/cadastro/completar", element: L(<CompletarCadastro />) },
          { path: "/esqueci-senha", element: L(<EsqueciSenha />) },
        ],
      },
    ],
  },
  // Redefinir senha vale com ou sem sessão (link do e-mail).
  { element: <AuthLayout />, children: [{ path: "/redefinir-senha", element: L(<RedefinirSenha />) }] },
  {
    element: <RotaProtegida exigir="OWNER" />,
    children: [
      {
        element: <PainelLayout />,
        children: [
          { path: "/", element: L(<Dashboard />) },
          { path: "/numeros", element: L(<ListaNumeros />) },
          { path: "/numeros/:id", element: L(<DetalheNumero />) },
          { path: "/numeros/:id/automacao", element: L(<AutoRespostas />) },
          { path: "/campanhas", element: L(<ListaCampanhas />) },
          { path: "/campanhas/nova", element: L(<NovaCampanha />) },
          { path: "/campanhas/:id", element: L(<DetalheCampanha />) },
          { path: "/listas", element: L(<Listas />) },
          { path: "/leads", element: L(<Leads />) },
          { path: "/leads/repasse", element: L(<ConfigRepasse />) },
          { path: "/caixa", element: L(<Caixa />) },
          { path: "/integracoes", element: L(<Integracoes />) },
          { path: "/cobranca", element: L(<Cobranca />) },
          { path: "/sobre", element: L(<Sobre />) },
          { path: "/cobranca/assinatura-necessaria", element: L(<BloqueioAssinatura />) },
        ],
      },
    ],
  },
  {
    element: <RotaProtegida exigir="SUPERADMIN" />,
    children: [
      {
        element: <PlataformaLayout />,
        children: [
          { path: "/plataforma", element: L(<PlataformaVisaoGeral />) },
          { path: "/plataforma/contas", element: L(<PlataformaContas />) },
          { path: "/plataforma/contas/:id", element: L(<PlataformaDetalheConta />) },
          { path: "/plataforma/planos", element: L(<PlataformaPlanos />) },
          { path: "/plataforma/numeros", element: L(<PlataformaNumeros />) },
          { path: "/plataforma/precos-meta", element: L(<PlataformaPrecos />) },
          { path: "/plataforma/configuracoes", element: L(<PlataformaConfig />) },
          { path: "/plataforma/auditoria", element: L(<PlataformaAuditoria />) },
        ],
      },
    ],
  },
  { path: "*", element: <Navigate to="/" replace /> },
], { basename: import.meta.env.BASE_URL.replace(/\/$/, "") });
